const HTML_MARKUP_PATTERN = /<!--|<!doctype|<\/?[a-z][^>]*>/i;
const HTML_TAG_PATTERN = /<\/?[a-z][^>]*>/gi;
const NON_CONTENT_TAGS = [
  'script',
  'style',
  'template',
  'iframe',
  'object',
  'embed',
  'svg',
  'canvas',
] as const;
const BLOCK_TAG_PATTERN =
  /<\/?(?:address|article|aside|blockquote|div|dl|dt|dd|fieldset|figcaption|figure|footer|form|h[1-6]|header|hr|main|nav|ol|p|pre|section|table|tbody|thead|tfoot|tr|ul)\b[^>]*>/gi;

const NAMED_ENTITIES: Readonly<Record<string, string>> = {
  amp: '&',
  apos: "'",
  copy: '©',
  gt: '>',
  hellip: '…',
  lt: '<',
  mdash: '—',
  nbsp: ' ',
  ndash: '–',
  quot: '"',
  reg: '®',
  trade: '™',
};

const decodeHtmlEntities = (value: string): string =>
  value.replace(
    /&(#x[\da-f]+|#\d+|[a-z][a-z\d]+);/gi,
    (entity, encoded: string) => {
      if (encoded[0] !== '#') {
        return NAMED_ENTITIES[encoded.toLowerCase()] ?? entity;
      }

      const radix = encoded[1]?.toLowerCase() === 'x' ? 16 : 10;
      const digits = radix === 16 ? encoded.slice(2) : encoded.slice(1);
      const codePoint = Number.parseInt(digits, radix);
      if (
        !Number.isFinite(codePoint) ||
        codePoint < 0 ||
        codePoint > 0x10ffff ||
        (codePoint >= 0xd800 && codePoint <= 0xdfff)
      ) {
        return entity;
      }

      return String.fromCodePoint(codePoint);
    },
  );

const readSafeLinkTarget = (attributes: string): string | null => {
  const hrefMatch = attributes.match(
    /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/i,
  );
  const href = decodeHtmlEntities(
    hrefMatch?.[1] ?? hrefMatch?.[2] ?? hrefMatch?.[3] ?? '',
  ).trim();

  return /^(?:https?:\/\/|www\.)/i.test(href) ? href : null;
};

const stripInlineMarkup = (value: string): string =>
  decodeHtmlEntities(
    value
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(HTML_TAG_PATTERN, ''),
  )
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Converts user-authored post content to the portable plain-text format used
 * by both native and web clients. Formatting tags are discarded, meaningful
 * block boundaries are retained, and safe link destinations remain visible.
 */
export function convertPostContentToPlainText(value: string): string {
  const normalized = value.replace(/\r\n?/g, '\n');
  if (!HTML_MARKUP_PATTERN.test(normalized)) return normalized.trim();

  let text = normalized
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<!doctype[^>]*>/gi, '');

  NON_CONTENT_TAGS.forEach(tag => {
    text = text.replace(
      new RegExp(`<${tag}\\b[^>]*>[\\s\\S]*?<\\/${tag}\\s*>`, 'gi'),
      '',
    );
  });

  text = text.replace(
    /<a\b([^>]*)>([\s\S]*?)<\/a\s*>/gi,
    (_match, attributes: string, content: string) => {
      const label = stripInlineMarkup(content);
      const href = readSafeLinkTarget(attributes);
      if (!href || label === href) return label;
      return label ? `${label} (${href})` : href;
    },
  );

  text = text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<li\b[^>]*>/gi, '• ')
    .replace(/<\/li\s*>/gi, '\n')
    .replace(/<\/?(?:td|th)\b[^>]*>/gi, ' ')
    .replace(BLOCK_TAG_PATTERN, '\n')
    .replace(HTML_TAG_PATTERN, '');

  return decodeHtmlEntities(text)
    .replace(/[^\S\n]+$/gm, '')
    .replace(/^[^\S\n]+/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
